import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LifeBuoy, Mail, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-200" data-testid="contact-page">
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="contact-home-link" className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-6 w-6 object-contain" /> StreamWorship
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 border border-amber-500/30">
            <LifeBuoy className="h-6 w-6 text-amber-400" />
          </span>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-white">Contact &amp; Support</h1>
            <p className="text-sm text-gray-500 mt-1">We're here to help your ministry get on air.</p>
          </div>
        </div>

        <p className="mt-6 text-sm text-gray-400">
          Have a question about streaming, your account, or the Ascend TV App? Send us a message and our
          team will get back to you. You can also email us directly at{" "}
          <a href="mailto:support@streamworship.org" className="text-amber-400 hover:underline">support@streamworship.org</a>.
        </p>

        {sent ? (
          <div data-testid="contact-success" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <h2 className="mt-3 font-display text-lg font-semibold text-white">Message sent</h2>
            <p className="mt-1 text-sm text-gray-400">Thanks for reaching out — we'll reply to {form.email} soon.</p>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:border-amber-500/40">
              Back to home
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5 rounded-2xl border border-gray-800 bg-[#0E131F] p-6" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <Label className="text-gray-300">Your name</Label>
                <Input value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="contact-name" className="mt-1.5 bg-black/30 border-gray-800" />
              </div>
              <div>
                <Label className="text-gray-300">Email</Label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder="you@church.org" data-testid="contact-email" className="mt-1.5 bg-black/30 border-gray-800" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Church / organization <span className="text-gray-600">(optional)</span></Label>
              <Input value={form.organization} onChange={set("organization")} placeholder="Grace Chapel" data-testid="contact-org" className="mt-1.5 bg-black/30 border-gray-800" />
            </div>
            <div>
              <Label className="text-gray-300">How can we help?</Label>
              <textarea
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder="Tell us what you need…"
                data-testid="contact-message"
                className="mt-1.5 w-full rounded-md border border-gray-800 bg-black/30 px-3 py-2 text-sm text-gray-100 focus:border-amber-500/40 focus:outline-none resize-y"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        )}

        <div className="mt-12 border-t border-white/10 pt-6 text-xs text-gray-600">
          © 2026 StreamWorship · <Link to="/privacy" className="hover:text-gray-400 underline underline-offset-2">Privacy Policy</Link> · <Link to="/terms" className="hover:text-gray-400 underline underline-offset-2">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
}
